import { Injectable } from '@angular/core';

import { filter } from 'rxjs';

import { environment } from '@environment';

import { SystemService } from '@system/services';

import { ConsoleTransport } from './console-transport';
import { LOGLEVEL } from './constants';
import { HttpTransport } from './http-transport';
import type { LogEntry, LogLevel, LoggerTransport } from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  level: LogLevel = environment.production ? LOGLEVEL.INFO : LOGLEVEL.DEBUG3;
  transports: LoggerTransport[];

  constructor(private systemService: SystemService) {}

  init() {
    if (environment.production) {
      this.transports = [new ConsoleTransport(), new HttpTransport()];

      this.systemService.systemDefaults$.pipe(filter((x) => !!x)).subscribe((systemDefaults) => {
        this.level = (systemDefaults.SA_LOG_LEVEL as LogLevel) ?? this.level;
      });
    } else {
      this.transports = [new HttpTransport()];
    }
  }

  error(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.ERROR, message, params);
  }

  warn(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.WARN, message, params);
  }

  info(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.INFO, message, params);
  }

  log(level: LogLevel, message: string, ...params: any[]) {
    this.writeToLog(level, message, params);
  }

  debug(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.DEBUG1, message, params);
  }

  debug1(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.DEBUG1, message, params);
  }

  debug2(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.DEBUG2, message, params);
  }

  debug3(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.DEBUG3, message, params);
  }

  trace(message: string, ...params: any[]) {
    this.writeToLog(LOGLEVEL.TRACE, message, params);
  }

  writeToLog(level: LogLevel, message: string, params: any[]) {
    if (this.shouldLog(level)) {
      const logEntry: LogEntry = { level, message, params, date: new Date().toISOString() };

      for (const transport of this.transports) {
        transport.log(logEntry);
      }
    }
  }

  shouldLog(level: LogLevel) {
    if (this.toNumber(level) <= this.toNumber(this.level)) {
      return true;
    } else {
      return false;
    }
  }

  toNumber(level: LogLevel) {
    switch (level) {
      case LOGLEVEL.TRACE:
        return 7;
      case LOGLEVEL.DEBUG3:
        return 6;
      case LOGLEVEL.DEBUG2:
        return 5;
      case LOGLEVEL.LOG:
      case LOGLEVEL.DEBUG:
      case LOGLEVEL.DEBUG1:
        return 4;
      case LOGLEVEL.INFO:
        return 3;
      case LOGLEVEL.WARN:
        return 2;
      case LOGLEVEL.ERROR:
        return 1;
      case LOGLEVEL.DISABLE:
      default:
        return 0;
    }
  }
}
